import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { UserResource } from '../types';

interface AuthContextType {
  user: UserResource | null;
  isAuthenticated: boolean;
  canManage: boolean;
  isSales: boolean;
  isMarketingManager: boolean;
  login: (token: string, user: UserResource) => void;
  logout: () => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<UserResource | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is already logged in
    const token = localStorage.getItem('authToken');
    const userData = localStorage.getItem('user');
    
    if (token && userData) {
      try {
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
      } catch (error) {
        console.error('Error parsing user data:', error);
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
      }
    }
    
    setLoading(false);
  }, []);

  const login = (accessToken: string, userData: UserResource) => {
    console.log('AuthContext: Setting user data:', userData);
    console.log('AuthContext: Setting access token:', accessToken);
    
    localStorage.setItem('authToken', accessToken);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    
    console.log('AuthContext: User state updated, isAuthenticated should be:', !!userData);
  };

  const logout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    setUser(null);
  };

  const canManage = user?.adminRole === 'full';
  const isSales = user?.adminRole === 'sales';
  const isMarketingManager = user?.adminRole === 'marketing';

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    canManage,
    isSales,
    isMarketingManager,
    login,
    logout,
    loading,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
